import { FC, useState } from 'react';
import { AdminLayout } from '@/components/admin/AdminLayout';
import { 
  MessageSquare, 
  AlertTriangle, 
  Shield, 
  Plus, 
  Flag,
  Clock,
  Search, 
  Filter, 
  Archive, 
  Trash2, 
  Eye 
} from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { useToast } from '@/components/ui/use-toast';
import { useQueryClient } from '@tanstack/react-query';
import { Checkbox } from '@/components/ui/checkbox';
import { AdminCommunityFeed } from '@/components/admin/AdminCommunityFeed';
import { useAuth } from "@/hooks/useAuth";
import { Users2 } from 'lucide-react';

interface ContentReport {
  id: string;
  content_type: string;
  reported_content: string;
  reason: string;
  status: string;
  severity: string;
  created_at: string;
  resolved_at: string | null;
  action_taken: string | null;
  reporter_name: string;
  reported_user_name: string | null;
  reporter_email: string;
  reported_user_email: string | null;
}

interface ModRule {
  id: string;
  word: string;
  type: 'blocked' | 'warning';
  category: string;
  created_at: string;
}

interface CommunityPost {
  id: string;
  user_id: string;
  content: string;
  created_at: string;
  tutor_name: string;
  student_name: string;
  email: string;
}

interface ModerationHistoryItem {
  id: string;
  type: 'report' | 'moderation';
  post_id: string;
  user_id: string;
  moderator_id: string | null;
  action: string;
  reason: string;
  content: string;
  created_at: string;
  user_name: string;
  user_email: string;
  moderator_name: string | null;
  reporter_id: string | null;
}

export const AdminCommunity = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState<'posts' | 'reports' | 'rules'>('posts');
  const [searchTerm, setSearchTerm] = useState('');
  const [newRule, setNewRule] = useState({ word: '', type: 'warning', category: 'inappropriate' });
  const [moderationDialog, setModerationDialog] = useState<{
    isOpen: boolean;
    postId: string | null;
    action: 'archive' | 'delete' | null;
    userId?: string;
    reason: string;
  }>({
    isOpen: false,
    postId: null,
    action: null,
    reason: '',
  });
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [postToDelete, setPostToDelete] = useState<string | null>(null);

  const { data: reports, isLoading: reportsLoading, refetch: refetchReports } = useQuery({
    queryKey: ['adminContentReports'],
    queryFn: async () => {
      // Primero intentamos usar la función RPC
      const { data: rpcData, error: rpcError } = await supabase.rpc('get_all_content_reports');
      
      if (!rpcError && rpcData) {
        return rpcData as ContentReport[];
      }
      
      // Si falla la RPC, hacemos la consulta directa a la vista
      const { data: viewData, error: viewError } = await supabase
        .from('parental_content_reports')
        .select('*')
        .order('created_at', { ascending: false });
      
      if (viewError) throw viewError;
      return viewData as ContentReport[];
    },
  });

  const pendingReportsCount = reports?.filter(report => report.status === 'pending').length || 0;

  const { data: rules, isLoading: rulesLoading, refetch: refetchRules } = useQuery({
    queryKey: ['moderationRules'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('content_moderation_rules')
        .select('*')
        .order('created_at', { ascending: false });
      if (error) throw error;
      return data as ModRule[];
    },
  });

  const { data: posts, isLoading } = useQuery({
    queryKey: ['adminPosts'],
    queryFn: async () => {
      console.log('Fetching posts in AdminCommunity');
      
      // Obtener todos los posts usando la función RPC
      const { data, error } = await supabase.rpc('get_all_posts_for_admin');

      if (error) {
        console.error('Error fetching posts:', error);
        throw error;
      }

      console.log('Posts fetched:', {
        total: data?.length,
        archived: data?.filter(p => p.archived).length,
        active: data?.filter(p => !p.archived).length,
        sample: data?.slice(0, 3).map(p => ({
          id: p.id,
          content: p.content.substring(0, 20),
          archived: p.archived,
          archived_at: p.archived_at,
          author: p.author
        }))
      });
      
      return data || [];
    },
    refetchOnWindowFocus: true,
    refetchOnMount: true
  });

  const handleUpdateStatus = async (reportId: string, newStatus: string, actionTaken: string) => {
    try {
      const shouldHideContent = newStatus === 'resolved';
      
      const { error } = await supabase.rpc('handle_report_resolution', {
        p_report_id: reportId,
        p_action: newStatus,
        p_action_taken: actionTaken,
        p_hide_content: shouldHideContent
      });
      
      if (error) throw error;
      
      toast({
        title: newStatus === 'resolved' ? "Reporte resuelto" : "Reporte descartado",
        description: newStatus === 'resolved' 
          ? "Se han tomado las medidas necesarias y se ha notificado a los usuarios involucrados."
          : "El reporte ha sido descartado y se ha notificado al reportador.",
      });
      
      refetchReports();
    } catch (error) {
      console.error('Error handling report:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "No se pudo procesar el reporte. Por favor, intenta nuevamente.",
      });
    }
  };

  const handleAddRule = async () => {
    try {
      const { error } = await supabase
        .from('content_moderation_rules')
        .insert([newRule]);
      
      if (error) throw error;
      
      toast({
        title: "Regla agregada",
        description: "La regla de moderación ha sido agregada exitosamente.",
      });
      
      setNewRule({ word: '', type: 'warning', category: 'inappropriate' });
      refetchRules();
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "No se pudo agregar la regla de moderación.",
      });
    }
  };

  const handleDeletePost = async (postId: string) => {
    try {
      if (!user?.id) return;

      const { data, error } = await supabase.rpc('delete_community_post', {
        p_post_id: postId,
        p_deleted_by: user.id
      });

      if (error) throw error;

      if (!data.success) {
        toast({
          variant: "destructive",
          title: "Error",
          description: data.message || "No se pudo eliminar el post.",
        });
        return;
      }

      // Actualizar la UI
      queryClient.invalidateQueries(['adminPosts']);

      toast({
        title: "Post eliminado",
        description: data.message,
      });

    } catch (error) {
      console.error('Error deleting post:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "No se pudo eliminar el post. Por favor, intenta de nuevo.",
      });
    } finally {
      setDeleteDialogOpen(false);
      setPostToDelete(null);
    }
  };

  const handleDeleteComment = async (commentId: string) => {
    try {
      if (!user?.id) return;

      const { data, error } = await supabase.rpc('delete_post_comment', {
        p_comment_id: commentId,
        p_deleted_by: user.id
      });

      if (error) throw error;

      if (!data.success) {
        toast({
          variant: "destructive",
          title: "Error",
          description: data.message || "No se pudo eliminar el comentario.",
        });
        return;
      }

      // Actualizar la UI
      queryClient.invalidateQueries(['adminPosts']);

      toast({
        title: "Comentario eliminado",
        description: data.message,
      });

    } catch (error) {
      console.error('Error deleting comment:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "No se pudo eliminar el comentario. Por favor, intenta de nuevo.",
      });
    }
  };

  const handleModerate = async (id: string, action: 'archive' | 'delete', userId: string, isComment: boolean = false) => {
    try {
      if (!user?.id) return;

      if (action === 'delete') {
        if (isComment) {
          await handleDeleteComment(id);
          return;
        }
        
        setPostToDelete(id);
        setDeleteDialogOpen(true);
      } else if (action === 'archive') {
        const { error } = await supabase.rpc('archive_community_post', {
          p_post_id: id,
          p_archived_by: user.id
        });

        if (error) throw error;

        // Actualizar la UI
        queryClient.invalidateQueries(['adminPosts']);

        toast({
          title: "Post archivado",
          description: "El post ha sido archivado exitosamente.",
        });
      }
    } catch (error) {
      console.error('Error moderating post:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "No se pudo completar la acción. Por favor, intenta de nuevo.",
      });
    }
  };

  const executeModeration = async () => {
    try {
      const { postId, action, userId, reason } = moderationDialog;
      if (!postId || !action || !reason) {
        toast({
          variant: "destructive",
          title: "Error",
          description: "Por favor, especifica un motivo para la moderación",
        });
        return;
      }

      console.log('Iniciando moderación:', { postId, action, userId, reason });

      // Verificar que el post exista y obtener sus datos
      const { data: post, error: postFetchError } = await supabase
        .from('community_posts')
        .select('*')
        .eq('id', postId)
        .single();

      if (postFetchError) {
        console.error('Error al obtener el post:', postFetchError);
        throw postFetchError;
      }

      if (!post) {
        throw new Error('Post no encontrado');
      }

      if (action === 'delete') {
        // Primero crear el registro de moderación
        console.log('Creando registro de moderación');
        const { error: moderationError } = await supabase
          .from('moderation_logs')
          .insert([
            {
              post_id: postId,
              user_id: userId,
              action: 'delete',
              reason: reason,
              content: post.content,
              moderator_id: user?.id
            }
          ]);

        if (moderationError) {
          console.error('Error al crear registro de moderación:', moderationError);
          throw moderationError;
        }

        // Luego eliminar comentarios
        console.log('Eliminando comentarios para el post:', postId);
        const { error: commentsError, data: deletedComments } = await supabase.rpc(
          'delete_post_comments',
          { p_post_id: postId }
        );

        if (commentsError) {
          console.error('Error al eliminar comentarios:', commentsError);
          throw commentsError;
        }
        console.log('Comentarios eliminados:', deletedComments);

        // Finalmente eliminar el post
        console.log('Eliminando post:', postId);
        const { error: postError, data: deletedPost } = await supabase.rpc(
          'delete_community_post',
          { p_post_id: postId }
        );

        if (postError) {
          console.error('Error al eliminar post:', postError);
          throw postError;
        }
        console.log('Post eliminado:', deletedPost);

        // Notificar al usuario
        if (userId) {
          console.log('Creando notificación para el usuario:', userId);
          const { error: notificationError } = await supabase.rpc(
            'create_moderation_notification',
            {
              p_user_id: userId,
              p_title: '¡Importante! Sobre tu publicación',
              p_message: `Tu publicación ha sido eliminada porque ${reason}. Recuerda mantener un ambiente respetuoso y seguro en nuestra comunidad.`,
              p_reference_id: postId,
              p_metadata: {
                action: 'moderation',
                post_id: postId,
                reason: reason,
                content: post.content,
                type: 'delete'
              }
            }
          );

          if (notificationError) {
            console.error('Error al crear notificación:', notificationError);
          } else {
            console.log('Notificación creada exitosamente');
          }
        }

      } else if (action === 'archive') {
        // Primero crear el registro de moderación
        console.log('Creando registro de moderación');
        const { error: moderationError } = await supabase
          .from('moderation_logs')
          .insert([
            {
              post_id: postId,
              user_id: userId,
              action: 'archive',
              reason: reason,
              content: post.content,
              moderator_id: user?.id
            }
          ]);

        if (moderationError) {
          console.error('Error al crear registro de moderación:', moderationError);
          throw moderationError;
        }

        // Luego archivar el post
        console.log('Archivando post:', postId);
        const { error: archiveError } = await supabase.rpc(
          'archive_community_post',
          { p_post_id: postId }
        );

        if (archiveError) {
          console.error('Error al archivar post:', archiveError);
          throw archiveError;
        }
        console.log('Post archivado exitosamente');

        // Notificar al usuario
        if (userId) {
          console.log('Creando notificación para el usuario:', userId);
          const { error: notificationError } = await supabase.rpc(
            'create_moderation_notification',
            {
              p_user_id: userId,
              p_title: 'Aviso sobre tu publicación',
              p_message: `Tu publicación ha sido archivada porque ${reason}. Podrás seguir participando en la comunidad siguiendo nuestras guías.`,
              p_reference_id: postId,
              p_metadata: {
                action: 'moderation',
                post_id: postId,
                reason: reason,
                content: post.content,
                type: 'archive'
              }
            }
          );

          if (notificationError) {
            console.error('Error al crear notificación:', notificationError);
            throw notificationError;
          }
          console.log('Notificación creada exitosamente');
        }
      }

      // Cerrar el diálogo y mostrar mensaje de éxito
      setModerationDialog({ isOpen: false, postId: '', action: null, userId: '', reason: '' });
      
      // Invalidar las consultas para actualizar la UI
      queryClient.invalidateQueries(['adminPosts']);
      queryClient.invalidateQueries(['moderationHistory']);
      
      toast({
        title: action === 'delete' ? 'Publicación eliminada' : 'Publicación archivada',
        description: action === 'delete' 
          ? 'La publicación ha sido eliminada y se ha notificado al usuario.'
          : 'La publicación ha sido archivada.',
      });

    } catch (error) {
      console.error('Error en moderación:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "No se pudo procesar la moderación. Por favor, intenta nuevamente.",
      });
    }
  };

  const filteredReports = reports?.filter(report => {
    const matchesSearch = 
      report.reported_content.toLowerCase().includes(searchTerm.toLowerCase()) ||
      report.student_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      report.tutor_name.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesSearch;
  });

  const renderTabs = () => (
    <div className="flex gap-2 mb-6">
      <Button
        variant={activeTab === 'posts' ? 'default' : 'outline'}
        onClick={() => setActiveTab('posts')}
      >
        <MessageSquare className="h-4 w-4 mr-2" />
        Publicaciones
      </Button>
      <Button
        variant={activeTab === 'reports' ? 'default' : 'outline'}
        onClick={() => setActiveTab('reports')}
        className="relative"
      >
        <Flag className="h-4 w-4 mr-2" />
        Reportes
        {pendingReportsCount > 0 && (
          <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
            {pendingReportsCount}
          </span>
        )}
      </Button>
      <Button
        variant={activeTab === 'rules' ? 'default' : 'outline'}
        onClick={() => setActiveTab('rules')}
      >
        <AlertTriangle className="h-4 w-4 mr-2" />
        Reglas
      </Button>
    </div>
  );

  const renderPendingReportsAlert = () => {
    if (activeTab === 'reports' && pendingReportsCount > 0) {
      return (
        <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-6">
          <div className="flex">
            <AlertTriangle className="h-5 w-5 text-yellow-400 mr-2" />
            <div>
              <p className="text-sm text-yellow-700">
                Tienes {pendingReportsCount} {pendingReportsCount === 1 ? 'reporte pendiente' : 'reportes pendientes'} de revisar
              </p>
            </div>
          </div>
        </div>
      );
    }
    return null;
  };

  const renderReportsList = () => {
    if (reportsLoading) {
      return (
        <div className="flex justify-center items-center h-40">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      );
    }

    if (!reports?.length) {
      return (
        <div className="text-center py-8">
          <Shield className="h-12 w-12 text-gray-400 mx-auto mb-3" />
          <p className="text-gray-500">No hay reportes para mostrar</p>
        </div>
      );
    }

    return (
      <div className="space-y-4">
        {reports.map((report) => (
          <Card key={report.id} className={`p-4 ${report.status === 'pending' ? 'border-yellow-400 bg-yellow-50' : ''}`}>
            <div className="flex items-center justify-between">
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <Badge variant={
                    report.status === 'pending' ? 'destructive' : 
                    report.status === 'resolved' ? 'default' : 
                    'secondary'
                  }>
                    {report.status === 'pending' ? 'Pendiente' : 
                     report.status === 'resolved' ? 'Resuelto' : 
                     report.status === 'dismissed' ? 'Descartado' : report.status}
                  </Badge>
                  <Badge variant={report.severity === 'high' ? 'destructive' : 
                    report.severity === 'medium' ? 'warning' : 
                    report.severity === 'low' ? 'success' : 'secondary'}>
                    {report.severity === 'high' ? 'Alta' :
                     report.severity === 'medium' ? 'Media' :
                     report.severity === 'low' ? 'Baja' : report.severity}
                  </Badge>
                  <span className="text-sm text-gray-500">
                    {format(new Date(report.created_at), "d 'de' MMMM 'a las' HH:mm", { locale: es })}
                  </span>
                </div>
                <p className="text-sm font-medium mb-1">
                  Reportado por: {report.reporter_name} ({report.reporter_email})
                </p>
                {report.reported_user_name && (
                  <p className="text-sm text-gray-600 mb-1">
                    Usuario reportado: {report.reported_user_name} ({report.reported_user_email})
                  </p>
                )}
                <p className="text-sm text-gray-600 mb-2">Razón: {report.reason}</p>
                <div className="bg-gray-50 p-3 rounded-md mt-2">
                  <p className="text-sm">{report.reported_content}</p>
                </div>
              </div>
              {report.status === 'pending' && (
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleUpdateStatus(report.id, 'dismissed', 'No action needed')}
                  >
                    Descartar
                  </Button>
                  <Button
                    variant="default"
                    size="sm"
                    onClick={() => handleUpdateStatus(report.id, 'resolved', 'Content reviewed and action taken')}
                  >
                    Resolver
                  </Button>
                </div>
              )}
            </div>
          </Card>
        ))}
      </div>
    );
  };

  return (
    <AdminLayout>
      <div className="container max-w-7xl mx-auto p-6">
        <div className="space-y-6">
          <div className="flex items-center mb-8">
            <div className="flex items-center gap-3">
              <Users2 className="w-8 h-8 text-primary" />
              <h1 className="text-3xl font-bold text-gray-900">Administración de Comunidad</h1>
            </div>
          </div>
          {renderTabs()}
          {renderPendingReportsAlert()}

          {activeTab === 'posts' && (
            <Card className="p-6">
              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <h2 className="text-2xl font-bold">Publicaciones de la Comunidad</h2>
                </div>
                <AdminCommunityFeed 
                  onModerate={handleModerate} 
                />
              </div>
            </Card>
          )}
          {activeTab === 'reports' && renderReportsList()}
          {activeTab === 'rules' && (
            <div className="mb-8">
              <Card className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <Shield className="w-5 h-5 text-primary" />
                    <h2 className="text-xl font-semibold text-gray-800">Reglas de Moderación</h2>
                  </div>
                  
                  <Dialog>
                    <DialogTrigger asChild>
                      <Button className="flex items-center gap-2">
                        <Plus className="w-4 h-4" />
                        Agregar Regla
                      </Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle>Agregar Nueva Regla</DialogTitle>
                        <DialogDescription>
                          Crea una nueva regla de moderación para el contenido de la comunidad.
                        </DialogDescription>
                      </DialogHeader>
                      
                      <div className="space-y-4 py-4">
                        <div className="space-y-2">
                          <label className="text-sm font-medium">Palabra o Frase</label>
                          <Input
                            value={newRule.word}
                            onChange={(e) => setNewRule({ ...newRule, word: e.target.value })}
                            placeholder="Ingresa la palabra o frase a moderar"
                          />
                        </div>
                        
                        <div className="space-y-2">
                          <label className="text-sm font-medium">Tipo</label>
                          <Select
                            value={newRule.type}
                            onValueChange={(value: 'blocked' | 'warning') => 
                              setNewRule({ ...newRule, type: value })
                            }
                          >
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="blocked">Bloqueado</SelectItem>
                              <SelectItem value="warning">Advertencia</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        
                        <div className="space-y-2">
                          <label className="text-sm font-medium">Categoría</label>
                          <Select
                            value={newRule.category}
                            onValueChange={(value) => 
                              setNewRule({ ...newRule, category: value })
                            }
                          >
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="inappropriate">Contenido Inapropiado</SelectItem>
                              <SelectItem value="bullying">Acoso</SelectItem>
                              <SelectItem value="personal_info">Información Personal</SelectItem>
                              <SelectItem value="spam">Spam</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      
                      <DialogFooter>
                        <Button onClick={handleAddRule}>Agregar Regla</Button>
                      </DialogFooter>
                    </DialogContent>
                  </Dialog>
                </div>

                <ScrollArea className="h-[200px]">
                  <div className="space-y-2">
                    {rulesLoading ? (
                      <div className="flex items-center justify-center h-20">
                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
                      </div>
                    ) : rules?.map((rule) => (
                      <Card key={rule.id} className="p-3 bg-white border border-gray-100">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="font-medium">{rule.word}</p>
                            <p className="text-sm text-gray-500">
                              {rule.category} • {rule.type === 'blocked' ? 'Bloqueado' : 'Advertencia'}
                            </p>
                          </div>
                          <Badge variant={rule.type === 'blocked' ? 'destructive' : 'warning'}>
                            {rule.type === 'blocked' ? 'Bloqueado' : 'Advertencia'}
                          </Badge>
                        </div>
                      </Card>
                    ))}
                  </div>
                </ScrollArea>
              </Card>
            </div>
          )}

          {/* Diálogo de Moderación */}
          <Dialog open={moderationDialog.isOpen} onOpenChange={(open) => !open && setModerationDialog(prev => ({ ...prev, isOpen: false }))}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Confirmar {moderationDialog.action === 'delete' ? 'eliminación' : 'archivo'}</DialogTitle>
                <DialogDescription>
                  {moderationDialog.action === 'delete' 
                    ? '¿Estás seguro de que deseas eliminar esta publicación? Esta acción no se puede deshacer y también eliminará todos los comentarios asociados.'
                    : '¿Estás seguro de que deseas archivar esta publicación? Podrás revertir esta acción más tarde.'}
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium">Motivo</label>
                  <Input
                    value={moderationDialog.reason}
                    onChange={(e) => setModerationDialog(prev => ({ ...prev, reason: e.target.value }))}
                    placeholder="Ingresa el motivo de la moderación"
                  />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setModerationDialog(prev => ({ ...prev, isOpen: false }))}>
                  Cancelar
                </Button>
                <Button
                  variant={moderationDialog.action === 'delete' ? 'destructive' : 'default'}
                  onClick={executeModeration}
                >
                  {moderationDialog.action === 'delete' ? 'Eliminar' : 'Archivar'}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* Diálogo de confirmación para eliminar */}
          <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Confirmar eliminación</DialogTitle>
                <DialogDescription>
                  ¿Estás seguro que deseas eliminar esta publicación? Esta acción no se puede deshacer y eliminará también todos los comentarios asociados.
                </DialogDescription>
              </DialogHeader>
              <DialogFooter>
                <Button
                  variant="outline"
                  onClick={() => {
                    setDeleteDialogOpen(false);
                    setPostToDelete(null);
                  }}
                >
                  Cancelar
                </Button>
                <Button
                  variant="destructive"
                  onClick={() => postToDelete && handleDeletePost(postToDelete)}
                >
                  Eliminar
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminCommunity;
